/**
 * Onboarding Tour — Impact Core
 *
 * Uses react-joyride to walk new users through key UI areas.
 * Steps target data-tour attributes placed on key components.
 */

import Joyride, { type CallBackProps, STATUS, type Step } from 'react-joyride';

interface OnboardingTourProps {
  run: boolean;
  onComplete: () => void;
}

export function OnboardingTour({ run, onComplete }: OnboardingTourProps) {
  const steps: Step[] = [
    {
      target: '[data-tour="sidebar"]',
      title: 'Navigation',
      content:
        'Use the sidebar to navigate between portfolio management, ESG data collection, compliance, reports, and intelligence tools.',
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="dashboard-kpis"]',
      title: 'Portfolio Overview',
      content:
        'These cards show your portfolio at a glance — total funds, AUM, active investments, and investee count.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="quick-actions"]',
      title: 'Quick Actions',
      content:
        'Create funds, add investments, invite investees, and generate reports with one click.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="language-selector"]',
      title: 'Language',
      content:
        'Switch between 24 official EU languages. The entire interface translates instantly.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="ai-chat-bubble"]',
      title: 'AI Assistant',
      content:
        'Ask questions about your portfolio anytime. The AI assistant has full context about your funds and investments.',
      placement: 'left',
    },
  ];

  const handleCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onComplete();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      showProgress
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#2563EB',
          zIndex: 10000,
          arrowColor: '#111827',
          backgroundColor: '#111827',
          textColor: '#F0F2F5',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
        },
        tooltip: {
          borderRadius: '12px',
          fontSize: '13px',
        },
        buttonNext: {
          borderRadius: '6px',
          fontSize: '13px',
          padding: '8px 16px',
        },
        buttonBack: {
          color: '#8E99AB',
          fontSize: '13px',
        },
        buttonSkip: {
          color: '#8E99AB',
          fontSize: '13px',
        },
      }}
      locale={{
        back: 'Previous',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
}
